import {GeneInfo} from "../types/Types";
import {useFileGenes} from "./useJsonLoader";

interface GeneByIdentifierResult {
  gene: GeneInfo | undefined;
  isLoaded: boolean;
  isEntrezId: boolean;
  isAlias: boolean;
}

/**
 * Looks up a gene by symbol, alias, or Entrez ID.
 * Symbol match takes priority over alias to prevent a gene's alias shadowing another gene's symbol.
 * If the identifier matches an alias or is a numeric Entrez ID, `isAlias`/`isEntrezId` are set
 * so the caller can redirect to the canonical symbol URL.
 *
 * @param identifier - Gene symbol, alias, or Entrez ID from the URL.
 */
const useGeneByIdentifier = (identifier: string | undefined): GeneByIdentifierResult => {
  const genes = useFileGenes();

  const isEntrezId = identifier !== undefined && !isNaN(Number(identifier)) && identifier.trim() !== "";

  const gene = (() => {
    if (!genes) return undefined;
    if (isEntrezId) return genes.find(g => g.e === Number(identifier));
    const identifierLower = identifier?.toLowerCase();
    return genes.find(g => g.s.toLowerCase() === identifierLower)
      ?? genes.find(g => g.a?.some(a => a.toLowerCase() === identifierLower));
  })();

  const isAlias = !isEntrezId && gene !== undefined && identifier !== undefined &&
    gene.s.toLowerCase() !== identifier.toLowerCase();

  return {gene, isLoaded: genes !== undefined, isEntrezId, isAlias};
};

export default useGeneByIdentifier;
